import React, { useState, useEffect, useRef } from 'react';
import { Card, Tabs, Table, Input, Space, Typography, Tag, Spin, message, Button } from 'antd';
import { SearchOutlined, AppstoreOutlined, UnorderedListOutlined, SettingOutlined, CheckCircleOutlined, CloseCircleOutlined, ApiOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { Product, Entitlement, OrgEntitlement, SKU, SKUOrgEntitlementConfig } from '../../types';
import type { ColumnsType } from 'antd/es/table';
import { ExportSnapshotButton, ImportSnapshotButton } from '../DataRelationships/ProductSnapshotActions';
import useRbac from '../../auth/rbac/useRbac';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface SKUConfigDisplay {
  sku_id: number;
  sku_name: string;
  product_id: number;
  product_name: string;
  operation?: string;
  operand?: number;
  force_region_name?: string;
  entitlements: string[];
}

interface ServiceConfigDisplay {
  sku_id: number;
  sku_name: string;
  org_entitlement_id: number;
  org_entitlement_name: string;
  org_entitlement_value: string;
  product_id: number;
  product_name: string;
  is_service: boolean;
}

const ViewData: React.FC = () => {
  const { isAdmin, isUser } = useRbac();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [orgEntitlements, setOrgEntitlements] = useState<OrgEntitlement[]>([]);
  const [skus, setSkus] = useState<SKU[]>([]);
  const [skuConfigs, setSkuConfigs] = useState<SKUConfigDisplay[]>([]);
  const [serviceConfigs, setServiceConfigs] = useState<ServiceConfigDisplay[]>([]);

  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const loadingRef = useRef(false);

  useEffect(() => {
    void loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      const [productsData, entitlementsData, orgEntitlementsData, skusData, skuConfigsData, skuConfigEntitlementsData, skuOrgEntitlementConfigsData] =
        await Promise.all([
          apiService.getProducts(),
          apiService.getEntitlements(),
          apiService.getOrgEntitlements(),
          apiService.getSKUs(),
          apiService.getProductSkuConfigs(),
          apiService.getProductSkuConfigEntitlements(),
          apiService.getSKUOrgEntitlementConfigs(),
        ]);

      setProducts(productsData || []);
      setEntitlements(entitlementsData || []);
      setOrgEntitlements(orgEntitlementsData || []);
      setSkus(skusData || []);

      // Build SKU configuration display data
      const configsDisplay: SKUConfigDisplay[] = (skuConfigsData || []).map((config: {
        sku_id: number;
        product_id: number;
        operation?: string;
        operand?: number;
        force_region_name?: string;
      }) => {
        const sku = skusData.find((s: SKU) => s.id === config.sku_id);
        const product = productsData.find((p: Product) => p.id === config.product_id);

        // Get entitlements for this config
        const configEntitlements = (skuConfigEntitlementsData || [])
          .filter((e: { product_sku_config_sku_id: number; product_sku_config_product_id: number; entitlement_id: number }) =>
            e.product_sku_config_sku_id === config.sku_id &&
            e.product_sku_config_product_id === config.product_id
          )
          .map((e: { entitlement_id: number }) => {
            const ent = entitlementsData.find((ent: Entitlement) => ent.id === e.entitlement_id);
            return ent ? `${ent.name}:${ent.value || 'N/A'}` : `ID: ${e.entitlement_id}`;
          });

        return {
          sku_id: config.sku_id,
          sku_name: sku?.name || `SKU ${config.sku_id}`,
          product_id: config.product_id,
          product_name: product?.name || `Product ${config.product_id}`,
          operation: config.operation,
          operand: config.operand,
          force_region_name: config.force_region_name,
          entitlements: configEntitlements,
        };
      });

      setSkuConfigs(configsDisplay);

      // Build Service Configuration display: org-entitlement ↔ SKU mapping
      const serviceConfigsDisplay: ServiceConfigDisplay[] = (skuOrgEntitlementConfigsData as SKUOrgEntitlementConfig[]).map((config) => {
        const sku = skusData.find((s: SKU) => s.id === config.sku_id);
        const orgEnt = orgEntitlementsData.find((oe: OrgEntitlement) => oe.id === config.org_entitlement_id);
        const product = productsData.find((p: Product) => p.id === orgEnt?.product_id);
        return {
          sku_id: config.sku_id,
          sku_name: sku?.name || `SKU ${config.sku_id}`,
          org_entitlement_id: config.org_entitlement_id,
          org_entitlement_name: orgEnt?.name || `Org Entitlement ${config.org_entitlement_id}`,
          org_entitlement_value: orgEnt?.value || '-',
          product_id: orgEnt?.product_id ?? 0,
          product_name: product?.name || '-',
          is_service: orgEnt?.is_service ?? false,
        };
      });
      setServiceConfigs(serviceConfigsDisplay);
    } catch (error) {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const filterData = <T,>(data: T[]): T[] => {
    if (!searchText) {return data;}

    const lowerSearch = searchText.toLowerCase();
    return data.filter(item =>
      Object.values(item as Record<string, unknown>).some(value =>
        String(value).toLowerCase().includes(lowerSearch)
      )
    );
  };

  const baseProductColumns: ColumnsType<Product> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      ellipsis: true,
    },
    {
      title: 'Provisioning Client',
      dataIndex: 'provisioning_client',
      key: 'provisioning_client',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Provisioning Style',
      dataIndex: 'provisioning_style',
      key: 'provisioning_style',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Is Hidden',
      dataIndex: 'is_hidden',
      key: 'is_hidden',
      width: 150,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
    {
      title: 'Is Auto Provision',
      dataIndex: 'is_auto_provision',
      key: 'is_auto_provision',
      width: 120,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
    {
      title: 'Is Unified Onboarding Supported',
      dataIndex: 'is_unified_onboarding_supported',
      key: 'is_unified_onboarding_supported',
      width: 120,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 155,
      fixed: 'right' as const,
      render: (_: unknown, row: Product) => (
        <ExportSnapshotButton
          productId={row.id as number}
          productName={row.name}
        />
      ),
    },
  ];

  const productColumns: ColumnsType<Product> = isAdmin || isUser
    ? baseProductColumns
    : baseProductColumns.filter((c) => c.key !== 'actions');

  const entitlementColumns: ColumnsType<Entitlement> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
    },
    {
      title: 'Product',
      dataIndex: 'product_id',
      key: 'product_id',
      width: 200,
      render: (productId: number) => {
        const product = products.find(p => p.id === productId);
        return product?.name || `ID: ${productId}`;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      sorter: (a, b) => (a.value || '').localeCompare(b.value || ''),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Needs Quantity',
      dataIndex: 'needs_quantity',
      key: 'needs_quantity',
      width: 150,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
    },
  ];

  const orgEntitlementColumns: ColumnsType<OrgEntitlement> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
    },
    {
      title: 'Product',
      dataIndex: 'product_id',
      key: 'product_id',
      width: 200,
      render: (productId: number) => {
        const product = products.find(p => p.id === productId);
        return product?.name || `ID: ${productId}`;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      sorter: (a, b) => (a.value || '').localeCompare(b.value || ''),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Needs Quantity',
      dataIndex: 'needs_quantity',
      key: 'needs_quantity',
      width: 150,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
    },
    {
      title: 'Is Service',
      dataIndex: 'is_service',
      key: 'is_service',
      width: 150,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
  ];

  const skuColumns: ColumnsType<SKU> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => (a.id || 0) - (b.id || 0),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Buying Model',
      dataIndex: 'subscription_buying_model',
      key: 'subscription_buying_model',
      ellipsis: true,
    },
    {
      title: 'Min Entitlement',
      dataIndex: 'min_entitlement',
      key: 'min_entitlement',
    },
    {
      title: 'Is External',
      dataIndex: 'is_external',
      key: 'is_external',
      width: 150,
      render: (value: boolean) => (
        value ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
  ];

  const skuConfigColumns: ColumnsType<SKUConfigDisplay> = [
    {
      title: 'SKU',
      dataIndex: 'sku_name',
      key: 'sku_name',
      width: 200,
      sorter: (a, b) => a.sku_name.localeCompare(b.sku_name),
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      render: (op: string) => op ? <Tag color="blue">{op}</Tag> : '-',
    },
    {
      title: 'Operand',
      dataIndex: 'operand',
      key: 'operand',
      width: 100,
      render: (val: number) => val ?? '-',
    },
    {
      title: 'Force Region',
      dataIndex: 'force_region_name',
      key: 'force_region_name',
      width: 120,
      render: (val: string) => val || '-',
    },
    {
      title: 'Entitlements',
      dataIndex: 'entitlements',
      key: 'entitlements',
      render: (entitlements: string[]) => (
        <Space wrap>
          {entitlements.length > 0 ? (
            entitlements.map((ent, idx) => (
              <Tag key={idx} color="green">{ent}</Tag>
            ))
          ) : (
            <Text type="secondary">None</Text>
          )}
        </Space>
      ),
    },
  ];

  const serviceConfigColumns: ColumnsType<ServiceConfigDisplay> = [
    {
      title: 'SKU',
      dataIndex: 'sku_name',
      key: 'sku_name',
      width: 200,
      sorter: (a, b) => a.sku_name.localeCompare(b.sku_name),
      render: (name: string) => <Tag color="blue">{name}</Tag>,
    },
    {
      title: 'Org Entitlement',
      dataIndex: 'org_entitlement_name',
      key: 'org_entitlement_name',
      sorter: (a, b) => a.org_entitlement_name.localeCompare(b.org_entitlement_name),
      render: (name: string) => <Tag color="purple">{name}</Tag>,
    },
    {
      title: 'Value',
      dataIndex: 'org_entitlement_value',
      key: 'org_entitlement_value',
      width: 120,
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
    },
    {
      title: 'Is Service',
      dataIndex: 'is_service',
      key: 'is_service',
      width: 110,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.is_service === value,
      render: (value: boolean) => (
        value
          ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
          : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
      ),
    },
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" tip="Loading data..." />
        </div>
      );
    }

    return (
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <AppstoreOutlined />
              Products ({products.length})
            </span>
          }
          key="products"
        >
          <Table
            dataSource={filterData(products)}
            columns={productColumns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <UnorderedListOutlined />
              Entitlements ({entitlements.length})
            </span>
          }
          key="entitlements"
        >
          <Table
            dataSource={filterData(entitlements)}
            columns={entitlementColumns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <UnorderedListOutlined />
              Org Entitlements ({orgEntitlements.length})
            </span>
          }
          key="org_entitlements"
        >
          <Table
            dataSource={filterData(orgEntitlements)}
            columns={orgEntitlementColumns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <AppstoreOutlined />
              SKUs ({skus.length})
            </span>
          }
          key="skus"
        >
          <Table
            dataSource={filterData(skus)}
            columns={skuColumns}
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <SettingOutlined />
              SKU Configurations ({skuConfigs.length})
            </span>
          }
          key="sku_configs"
        >
          <Table
            dataSource={filterData(skuConfigs)}
            columns={skuConfigColumns}
            rowKey={(record) => `${record.sku_id}-${record.product_id}`}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <ApiOutlined />
              Service Configurations ({serviceConfigs.length})
            </span>
          }
          key="service_configs"
        >
          <Table
            dataSource={filterData(serviceConfigs)}
            columns={serviceConfigColumns}
            rowKey={(record) => `${record.sku_id}-${record.org_entitlement_id}`}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} items` }}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            tableLayout="auto"
          />
        </TabPane>
      </Tabs>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title level={2}>View Data</Title>
              <Text type="secondary">Browse and search all products, entitlements, SKUs, and configurations</Text>
            </div>
            <Space>
              {(isAdmin || isUser) && (
                <ImportSnapshotButton
                  onImportComplete={() => {
                    loadingRef.current = false;
                    void loadAllData();
                  }}
                />
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadingRef.current = false;
                  void loadAllData();
                }}
              >
                Refresh
              </Button>
            </Space>
          </div>

          <Input
            placeholder="Search across all fields..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
            style={{ maxWidth: 600 }}
          />

          {renderContent()}
        </Space>
      </Card>
    </div>
  );
};

export default ViewData;
